/**
 * UnsupportedToolBlock - Graceful fallback for unrecognised tool calls.
 *
 * When the backend adds a new tool but the desktop app hasn't been updated
 * yet, this component renders a polished card so the user knows the agent
 * performed an action — even though the frontend can't render a rich UI
 * for it.
 *
 * Visual design mirrors existing tool blocks (ArtefactBlock, ClinicalTaskResultBlock):
 * - rounded-lg, border, bg-surface-accent
 * - status states: loading / completed / error
 * - design-system typography + icons + tokens
 */

import {
  TypographyCaption,
  TypographyP2,
} from '@heidi/design-system/typography';
import { cn } from '@/lib/cn';
import { useTranslations } from '@heidi/runtime/i18n';
import { AlertCircle, CircleCheck, Loader2, Puzzle } from 'lucide-react';

import { formatToolName } from '@heidi/evidence/data';

export interface UnsupportedToolBlockProps {
  /** The raw tool type string from the backend (e.g. "patient_lab_results") */
  toolType: string;
  /** Status string forwarded from the backend chunk */
  status?: string;
  /** Additional CSS classes */
  className?: string;
}

/** Statuses that indicate the tool is still in progress */
export const LOADING_STATUSES = new Set([
  'fetching',
  'searching',
  'generating',
  'calculating',
  'creating',
  'sending',
  'thinking',
  'streaming',
]);

/** Statuses that indicate an error */
const ERROR_STATUSES = new Set(['error', 'failed']);

/** Derive a visual state from the raw backend status string */
const getVisualState = (status?: string): 'loading' | 'completed' | 'error' => {
  if (!status) return 'completed';
  const normalised = status.toLowerCase();
  if (LOADING_STATUSES.has(normalised)) return 'loading';
  if (ERROR_STATUSES.has(normalised)) return 'error';
  return 'completed';
};

/**
 * UnsupportedToolBlock - Renders a generic card for unknown tool calls
 *
 * @example
 * <UnsupportedToolBlock toolType="patient_lab_results" status="completed" />
 */
export const UnsupportedToolBlock = ({
  toolType,
  status,
  className,
}: UnsupportedToolBlockProps): JSX.Element => {
  const { t } = useTranslations();
  const visualState = getVisualState(status);
  const displayName = formatToolName(toolType);
  const isError = visualState === 'error';
  const isLoading = visualState === 'loading';

  const caption = isError
    ? t('chat.unsupportedTool.actionFailed')
    : isLoading
      ? t('chat.unsupportedTool.actionRunning')
      : t('chat.unsupportedTool.actionCompleted');

  // Unified card shell — matches the agent-chat ``ToolCallRow``: a
  // status-tinted tile + label/status caption, with the "generic tool"
  // puzzle glyph as a quiet trailing hint.
  return (
    <div
      className={cn(
        'group/tool flex w-full min-w-0 items-center gap-3 rounded-xl border border-border bg-surface-accent px-3.5 py-3',
        'transition-all duration-300 motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-1',
        isError && 'border-validation-destructive-border bg-validation-destructive-muted/40',
        className
      )}
      data-testid="unsupported-tool-block"
      data-status={visualState}
      data-tool-type={toolType}
    >
      <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-surface text-text-secondary">
        <Puzzle className="size-4" aria-hidden="true" />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <TypographyP2 className="truncate font-semibold leading-tight text-text-primary">
          {displayName}
        </TypographyP2>
        <span className="flex items-center gap-1.5">
          {isLoading ? (
            <Loader2 className="size-3 shrink-0 animate-spin text-text-tertiary" />
          ) : isError ? (
            <AlertCircle className="size-3 shrink-0 text-validation-destructive" />
          ) : (
            <CircleCheck className="size-3 shrink-0 text-validation-success" />
          )}
          <TypographyCaption
            className={cn(
              'truncate leading-tight',
              isError ? 'text-validation-destructive' : 'text-text-tertiary'
            )}
          >
            {caption}
          </TypographyCaption>
        </span>
      </span>
    </div>
  );
};
